/**
 * Enviar anexo de prontuário sem mentir sobre o resultado.
 *
 * Sob carga, o gateway devolveu 504 DEPOIS de o arquivo ter sido gravado: a
 * tela dizia "não foi possível enviar", a pessoa reenviava, e o prontuário
 * ganhava duas cópias do mesmo exame. Duas coisas resolvem:
 *
 * - **o caminho sai do conteúdo** — o mesmo arquivo, no mesmo campo, cai no
 *   mesmo caminho; reenviar não cria outro;
 * - **erro de rede não é veredito** — antes de dizer que falhou, pergunta ao
 *   balde se o arquivo está lá.
 */

export interface ErroDoBalde {
    message: string;
    statusCode?: string | number;
}

/** O pedaço do cliente de storage que isto usa — e que o teste imita. */
export interface BaldeDeAnexos {
    upload(caminho: string, corpo: ArrayBuffer, opcoes: { contentType: string; upsert: boolean }): PromiseLike<{ error: ErroDoBalde | null }>;
    list(pasta: string, opcoes: { search: string; limit: number }): PromiseLike<{ data: { name: string }[] | null; error: ErroDoBalde | null }>;
}

/**
 * Impressão do conteúdo: FNV-1a de 32 bits mais o tamanho.
 *
 * Não é segurança, é identidade dentro de uma pasta que já é da clínica, do
 * paciente e do campo — basta distinguir arquivos diferentes com o mesmo nome.
 */
export function impressaoDosBytes(bytes: ArrayBuffer): string {
    const v = new Uint8Array(bytes);
    let h = 0x811c9dc5;
    for (let i = 0; i < v.length; i += 1) {
        h ^= v[i] ?? 0;
        h = Math.imul(h, 0x01000193) >>> 0;
    }
    return `${h.toString(16).padStart(8, '0')}${v.length.toString(36)}`;
}

/**
 * O caminho COMEÇA pela clínica: é o primeiro pedaço que a política do balde
 * compara com o vínculo de quem envia.
 */
export function caminhoDoAnexo(clinicaId: string, contatoId: string, uid: string, nomeDoArquivo: string, bytes: ArrayBuffer): string {
    const nomeLimpo = nomeDoArquivo.replace(/[^\w.-]+/g, '_').slice(-80);
    return `${clinicaId}/${contatoId}/${uid}/${impressaoDosBytes(bytes)}-${nomeLimpo}`;
}

function jaExiste(e: ErroDoBalde): boolean {
    return String(e.statusCode ?? '') === '409' || /already exists|duplicate/i.test(e.message);
}

async function estaNoBalde(balde: BaldeDeAnexos, caminho: string): Promise<boolean> {
    const corte = caminho.lastIndexOf('/');
    const pasta = caminho.slice(0, corte);
    const arquivo = caminho.slice(corte + 1);
    const { data, error } = await balde.list(pasta, { search: arquivo, limit: 100 });
    if (error) return false;
    return (data ?? []).some((o) => o.name === arquivo);
}

export async function enviarSemDuplicar(balde: BaldeDeAnexos, caminho: string, bytes: ArrayBuffer, tipo: string): Promise<void> {
    const { error } = await balde.upload(caminho, bytes, { contentType: tipo || 'application/octet-stream', upsert: false });
    if (!error) return;
    /* Mesmo conteúdo, mesmo caminho: é o reenvio do que já entrou. */
    if (jaExiste(error)) return;
    /* 504, queda de rede: o arquivo pode ter entrado antes da resposta sumir. */
    if (await estaNoBalde(balde, caminho)) return;
    throw new Error(error.message);
}
